import { fetchApi } from '@/lib/api';
import { factoryInit, type FactoryRequestOptions } from '@/lib/factory-request';
import { useAuthStore } from '@/stores/authStore';
import type { Me } from '@shared/types/factory';

/**
 * The signed-in user's own account (/api/me): who they are, their handle (@mentions), name and
 * photo. A name set here stops sign-in overwriting it from Google; an uploaded photo wins over the
 * one from their sign-in account. Changes show at once in the top bar (the auth store).
 */

export const MEMBER_PHOTO_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;
export const DISPLAY_NAME_MAX = 80;

export type MyProfile = {
  displayname: string | null;
  email: string;
  photourl: string | null;
  has_uploaded_photo: boolean;
};

/** The signed-in user, with the admin flag (GET /api/me). */
export function getMe(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ me: Me }>('/me', factoryInit(opts));
}

export function getMyProfile() {
  return fetchApi<{ profile: MyProfile }>('/me/profile');
}

export async function setMyName(displayname: string) {
  const res = await fetchApi<{ displayname: string }>('/me/name', {
    method: 'PUT',
    body: JSON.stringify({ displayname }),
  });
  if (res.data) useAuthStore.getState().updateMe({ displayname: res.data.displayname });
  return res;
}

/** `photo` is the cropped square from PhotoCropDialog. */
export async function uploadMyPhoto(photo: Blob) {
  if (photo.size > MAX_BYTES) return { data: null, error: 'Photos can be up to 5 MB' };
  const form = new FormData();
  form.append('file', photo, 'photo.jpg');
  const res = await fetchApi<{ photourl: string }>('/me/photo', { method: 'PUT', body: form });
  if (res.data) useAuthStore.getState().updateMe({ photourl: res.data.photourl });
  return res;
}

export async function removeMyPhoto() {
  const res = await fetchApi<{ photourl: string | null }>('/me/photo', { method: 'DELETE' });
  if (res.data) useAuthStore.getState().updateMe({ photourl: res.data.photourl });
  return res;
}

/** Your @mention name, unique across the factory. 400 INVALID_HANDLE, 409 HANDLE_TAKEN. */
export async function setMyHandle(handle: string) {
  const res = await fetchApi<{ handle: string; me: Me | null }>('/me/handle', {
    method: 'PUT',
    body: JSON.stringify({ handle }),
  });
  if (res.data) useAuthStore.getState().updateMe({ handle: res.data.handle });
  return res;
}
